/**
 * @fileoverview Authentication context for global auth state management
 * Handles user authentication via server-side JWT with HttpOnly cookies,
 * proactive token refresh, and automatic session recovery.
 * @author SKEMA CANADA
 */

import { createContext, useReducer, useEffect, useCallback, useRef } from "react";
import { authApi, parseAuthError } from "@services/authApi";
import { setSessionExpiredCallback } from "@services/api/apiConfig";

/**
 * Proactive refresh interval: 1h55min (5 minutes before the 2h JWT expiry)
 * @constant {number}
 */
const REFRESH_INTERVAL_MS = 115 * 60 * 1000;

/**
 * Authentication action types
 */
const AUTH_ACTIONS = {
  LOGIN_START: "LOGIN_START",
  LOGIN_SUCCESS: "LOGIN_SUCCESS",
  LOGIN_FAILURE: "LOGIN_FAILURE",
  LOGOUT: "LOGOUT",
  RESTORE_SESSION: "RESTORE_SESSION",
  CLEAR_ERROR: "CLEAR_ERROR",
  SET_LOADING: "SET_LOADING",
};

/**
 * Initial authentication state with default values
 * Loading starts as true for session restoration check
 */
const initialState = {
  user: null,
  isAuthenticated: false,
  loading: true,
  error: null,
};

/**
 * Authentication reducer for state management
 * @param {Object} state - Current authentication state
 * @param {Object} action - Action object with type and payload
 * @returns {Object} New authentication state
 */
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        loading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload.error,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };

    case AUTH_ACTIONS.RESTORE_SESSION:
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        loading: false,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    default:
      return state;
  }
};

/**
 * Authentication context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext(null);

/**
 * Authentication context provider component.
 * Manages auth state via server-side JWT stored in HttpOnly cookies.
 * Includes proactive token refresh and automatic session recovery on 401.
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that need auth context
 */
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const refreshTimerRef = useRef(null);

  /**
   * Start the proactive refresh timer.
   * Silently refreshes the JWT 5 minutes before expiration.
   */
  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }

    refreshTimerRef.current = setInterval(async () => {
      try {
        await authApi.refreshSession();
      } catch {
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      }
    }, REFRESH_INTERVAL_MS);
  }, []);

  /**
   * Stop the proactive refresh timer.
   */
  const stopRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  /**
   * Authenticate a user via the server-side login endpoint.
   * The server sets an HttpOnly cookie containing the JWT.
   * Starts the proactive refresh timer on success.
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<boolean>} Success status
   */
  const login = useCallback(async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const response = await authApi.login(credentials);

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user: response.user },
      });

      startRefreshTimer();
      return true;
    } catch (error) {
      const parsedError = parseAuthError(error);
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: { error: parsedError.message },
      });
      return false;
    }
  }, [startRefreshTimer]);

  /**
   * Log out the current user.
   * Stops the refresh timer and clears the server-side cookie.
   */
  const logout = useCallback(async () => {
    stopRefreshTimer();

    try {
      await authApi.logout();
    } catch (error) {
      console.error("Logout error:", error);
    }
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  }, [stopRefreshTimer]);

  /**
   * Check current authentication status by calling GET /api/auth/me.
   * Restores the session and starts the refresh timer if a valid cookie exists.
   * @returns {Promise<boolean>} Authentication status
   */
  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await authApi.verifySession();

      dispatch({
        type: AUTH_ACTIONS.RESTORE_SESSION,
        payload: { user: response.user },
      });

      startRefreshTimer();
      return true;
    } catch {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      return false;
    }
  }, [startRefreshTimer]);

  /**
   * Get current user information
   * @returns {Object|null} Current user data
   */
  const getCurrentUser = useCallback(() => {
    if (!state.isAuthenticated || !state.user) {
      return null;
    }

    return {
      id: state.user.id,
      username: state.user.username,
      email: state.user.email,
    };
  }, [state.isAuthenticated, state.user]);

  /**
   * Clear authentication error
   */
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  /**
   * Register the session expired callback and initialize auth on mount.
   * The callback allows the API interceptor to trigger logout on irrecoverable 401.
   */
  useEffect(() => {
    setSessionExpiredCallback(() => {
      stopRefreshTimer();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    });

    checkAuthStatus();

    return () => {
      stopRefreshTimer();
      setSessionExpiredCallback(null);
    };
  }, [checkAuthStatus, stopRefreshTimer]);

  /**
   * Context value object
   */
  const contextValue = {
    user: getCurrentUser(),
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    error: state.error,

    login,
    logout,
    checkAuthStatus,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
